<template>
<v-app
  id="app"
  :style="cssVars"
>
  <div
    id="main-content"
  >
    <WorldWideTelescope
      :wwt-namespace="wwtNamespace"
    ></WorldWideTelescope>
    
    

    <!-- This contains the splash screen content -->

    <v-overlay
      :model-value="showSplashScreen"
      absolute
      opacity="0.6"
      :style="cssVars"
      id="splash-overlay"
    >
      <div
        id="splash-screen"
        v-click-outside="closeSplashScreen"
        :style="cssVars"
      >
        <font-awesome-icon
          id="close-splash-button"
          @click="closeSplashScreen"
          icon="xmark"
        />
        <div id="splash-screen-text">
          <p>
            Want to surf a <span 
            :style="{'color': accentColor}"
            >giant wave</span> in the Milky Way Galaxy? 
            <!-- see the <span style="color: red;">Radcliffe Wave</span> <span class="color-span">oscillating</span> through our Galaxy? -->
          </p>
        </div>

        <div>
          <v-btn
            class="splash-get-started"
            @click="closeSplashScreen"
            :color="accentColor"
            :density="xSmallSize ? 'compact' : 'default'"
            size="x-large"
            variant="elevated"
            rounded="lg"
          >
            Get Started
          </v-btn>
        </div>

        <div id="splash-screen-acknowledgements">
          Brought to you by <a href="https://www.cosmicds.cfa.harvard.edu/" target="_blank" rel="noopener noreferrer">Cosmic Data Stories</a> and <a href="https://www.worldwidetelescope.org/home/" target="_blank" rel="noopener noreferrer">WorldWide Telescope</a>.
          
          <div id="splash-screen-logos">
            <credit-logos
              :default-logos="['cosmicds', 'wwt', 'sciact', 'nasa-grantee']"
            />
          </div>
        </div>
      </div>
    </v-overlay>

    <transition>
      <div
        v-if="false"
        class="no-background">
      </div>
      <div
        v-else
        :class="['modal', showSplashScreen ? 'no-background' : '']"
        id="modal-loading"
        v-show="isLoading || userNotReady"
      >
        <div v-if="isLoading" class="container">
          <div  class="spinner"></div>
          <p>Loading …</p>
        </div>
        <div v-else>
          <v-btn
            v-if="!showSplashScreen"
            id="loading-button"
            color="white"
            :disabled="isLoading"
            variant="outlined"
            @click="userNotReady = false"
            @keyup.enter="userNotReady = false"
            elevation="10"
            :size="smallSize ? 'large' : 'x-large'"
            rounded="lg"
            prepend-icon="mdi-check-circle-outline"
          >
            <strong>Start</strong>
          </v-btn>
        </div>  
      </div>
    </transition>

    <!-- This block contains the elements (e.g. icon buttons displayed at/near the top of the screen -->

    <div class="top-content">
      <div v-if="modeReactive != null" id="left-buttons">
        <icon-button
          fa-icon="house"
          @activate="() => {
            positionReset();
            timeReset();
          }"
          :color="buttonColor"
          tooltip-text="Reset this view"
          tooltip-location="start"
        ></icon-button>
        <icon-button
          v-model="showTextSheet"
          fa-icon="book-open"
          :color="buttonColor"
          :tooltip-text="showTextSheet ? 'Hide Info' : 'Learn More'"
          tooltip-location="start"
        >
        </icon-button>
        <icon-button
          v-model="showVideoSheet"
          fa-icon="video"
          :color="buttonColor"
          tooltip-text="Watch video"
          tooltip-location="start"
        >
        </icon-button>
      </div>
      <div id="center-buttons">
<!-- <p class="pointer-events"> {{ wwtPosition }} </p> -->
        <icon-button
          v-if="false"
          md-icon="mdi-sine-wave"
          @activate="toggleFullwaveMode()"
          :color="buttonColor"
          tooltip-text="Full Wave Mode"
          tooltip-location="bottom"
        >
        <template v-slot:button>
          <span v-if="modeReactive != 'full'" class="no-select">View the Full Radcliffe Wave</span>
          <v-icon v-if="modeReactive == 'full'">mdi-arrow-left</v-icon>
        </template>
        </icon-button>
        
        <icon-button
          v-if="(playCount > 0) ?? (modeReactive == 'full')"
          @activate="modeReactive = modeReactive == '3D' ? '2D' : '3D'"
          :color="buttonColor"
          tooltip-text="Switch modes"
          tooltip-location="start"
        >
        <template v-slot:button>
          <span class="no-select">See this {{ modeReactive == '3D' ? ' on the Sky (2D)' : 'in the Galaxy (3D)' }}</span>
        </template>
        </icon-button>
        <icon-button
          v-if="(playCount > 0) && modeReactive=='3D'"
          @activate="() => {
            positionReset();
            timeReset();
            playing=true;
          }"
          :color="buttonColor"
          tooltip-text="Replay Wave Motion"
          tooltip-location="end"
        >
        <template v-slot:button>
          Replay
        </template>
        </icon-button>
        
        
        
      </div>
      <div id="right-buttons">
                <!-- add a menu selector for background2DImageset -->
      </div>
    </div>


    <!-- This block contains the elements (e.g. the project icons) displayed along the bottom of the screen -->

    <div class="bottom-content">
      <div v-if="modeReactive != '2D'" id="time-controls">
        <icon-button
          id="play-pause-icon"
          :fa-icon="!(playing) ? 'play' : 'pause'"
          fa-size='xl'
          @activate="() => {
            playing = !(playing);
          }"
          :color="buttonColor"
          tooltip-text="Play/Pause"
          tooltip-location="top"
          tooltip-offset="5px"
          :show-tooltip="!mobile"
        ></icon-button>
        <input
          type="range"
          id="time-slider"
          min="0"
          max="720"
          :oninput="onInputChange"
        />
      </div>
      <div v-else id="time-controls" style="width:50%">
        <v-select
          v-if="modeReactive == '2D'"
          v-model="background2DImageset"
          :items="allSkyImagesets"
          label="Background"
          outlined
          hide-details
          :color="accentColor"
          class="pointer-events"
          />
      </div>
      <div id="body-logos" v-if= "!smallSize">
        <credit-logos
          :default-logos="['cosmicds', 'wwt', 'sciact', 'nasa-grantee']"
        />
      </div>
    </div>


    <!-- This dialog contains the video that is displayed when the video icon is clicked -->

    <v-dialog
      id="video-container"
      v-model="showVideoSheet"
      transition="slide-y-transition"
    >
      <div class="video-wrapper">
        <font-awesome-icon
          id="video-close-icon"
          class="close-icon"
          icon="times"
          size="lg"
          @click="showVideoSheet = false"
          @keyup.enter="showVideoSheet = false"
          tabindex="0"
        ></font-awesome-icon>
        <!-- <video
          controls
          id="info-video"
        >
          <source src="./assets/ralf_crop_small.mp4" type="video/mp4">
        </video> -->

        <iframe 
          id="info-video"
          width="560" height="315" 
          src="https://www.youtube.com/embed/NJhbdSy6nss?si=tdyFAO_6qo_qVlhz" 
          title="YouTube video player" 
          frameborder="0" 
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
          allowfullscreen>
        </iframe>
        </div>
    </v-dialog>


    <!-- This dialog contains the informational content that is displayed when the book icon is clicked -->

    <v-dialog
      :style="cssVars"
      class="bottom-sheet"
      id="text-bottom-sheet"
      hide-overlay
      persistent
      no-click-animation
      absolute
      width="100%"
      :scrim="false"
      location="bottom"
      v-model="showTextSheet"
      transition="dialog-bottom-transition"
    >
      <v-card height="100%">
        <v-tabs
          v-model="tab"
          height="32px"
          :color="accentColor"
          :slider-color="accentColor"
          id="tabs"
          dense
        >
          <v-tab class="info-tabs" tabindex="0"><h3>Information</h3></v-tab>
          <v-tab class="info-tabs" tabindex="0"><h3>Using WWT</h3></v-tab>
        </v-tabs>
        <font-awesome-icon
          id="close-text-icon"
          class="control-icon"
          icon="times"
          size="lg"
          @click="showTextSheet = false"
          @keyup.enter="showTextSheet = false"
          tabindex="0"
        ></font-awesome-icon>
        <v-window v-model="tab" id="tab-items" class="pb-2 no-bottom-border-radius">
          <v-window-item>
            <v-card class="no-bottom-border-radius scrollable">
              <v-card-text class="info-text no-bottom-border-radius">
                <h3>What is the RadWave?</h3>
                <p>The more humans study our universe, the more we find that it is richer and more complex than we previously realized. The Radcliffe Wave (or RadWave for short) is a massive structure that was discovered in our Milky Way galaxy by astronomers João Alves, Catherine Zucker, Alyssa Goodman, and others in 2020<sup><a href="#2020team" class="highlight">[1]</a></sup>. The RadWave is made up of gas, dust, and stars loosely connected in a wave-like shape. It is so huge and so close to us that earlier scientists did not see that these parts were all connected. Learn more about the discovery of the RadWave here!</p>

                <h3>How do scientists make predictions from models and data?</h3>
                <p>The RadWave looks like a wave, but does it also move like a wave? Ralf Konietzka, with Goodman, Zucker, and others<sup><a href="#2024team" class="highlight">[2]</a></sup> used data from the Gaia satellite to answer this question by measuring the speed and direction of stars in different parts of the wave. Just like weather reporters can predict where a storm will go based on wind patterns, Konietzka uses the information about the RadWave’s current motion to predict how it will move in the future. The best model that fits the data is shown in this interactive.</p>

                <h3>Why do the blue star clusters fade out?</h3>
                <p>The time it takes for the RadWave to complete a “wave” is about 100 million years, but the clouds and young star clusters along the wave will disperse in less than 30 million years, so the blue dots that represent the star clusters fade out in the interactive view to reflect this.</p>

                <h3>Science Credits</h3>
                <p>[1] <span class="highlight" id="2020team">2020 RadWave Team: </span>
                João Alves, Catherine Zucker, Alyssa A. Goodman, Joshua S. Speagle, Stefan Meingast, Thomas Robitaille, Douglas P. Finkbeiner, Edward F. Schlafly, and Gregory M. Green</p>
                <p>[2] <span class="highlight" id="2024team">2024 RadWave in Motion Team:</span>
                Ralf Konietzka, Alyssa A. Goodman, Catherine Zucker, Andreas Burkert, João Alves, Michael Foley, Cameren Swiggum, Maria Koller, and Núria Miret-Roig</p>
                <v-spacer class="end-spacer"></v-spacer>
              </v-card-text>
            </v-card>
          </v-window-item>
          <v-window-item>
            <v-card class="no-bottom-border-radius scrollable">
              <v-card-text class="info-text no-bottom-border-radius">
                <v-container>
                  <v-row align="center">
                  <v-col cols="4">
                      <v-chip
                        label
                        outlined
                      >
                        Pan
                      </v-chip>
                    </v-col>
                    <v-col cols="8" class="pt-1">
                      <strong>{{ touchscreen ? "press + drag" : "click + drag" }}</strong>  {{ touchscreen ? "" : "or" }}  <strong>{{ touchscreen ? "" : "W-A-S-D" }}</strong> {{ touchscreen ? "" : "keys" }}<br>
                    </v-col>
                  </v-row>
                  <v-row align="center">
                    <v-col cols="4">
                      <v-chip
                        label
                        outlined
                      >
                        Zoom
                      </v-chip>
                    </v-col>
                    <v-col cols="8" class="pt-1">
                      <strong>{{ touchscreen ? "pinch in and out" : "scroll in and out" }}</strong> {{ touchscreen ? "" : "or" }} <strong>{{ touchscreen ? "" : "I-O" }}</strong> {{ touchscreen ? "" : "keys" }}<br>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <div class="credits">
                        <h3>Science Credits:</h3>
                        Ralf Konietzka<br>
                        Catherine Zucker<br>
                        Alyssa A. Goodman<br>
                      </div>
                      <div class="credits">
                      <h3>Credits:</h3>
                      <p>This Data Story is powered by WorldWide Telescope.</p>
                      <h4><a href="https://www.cosmicds.cfa.harvard.edu/" target="_blank" rel="noopener noreferrer">CosmicDS</a> Team:</h4>
                      Jon Carifio<br>
                      John Lewis<br>
                      Pat Udomprasert<br>
                      Alyssa Goodman<br>
                      Mary Dussault<br>
                      Harry Houghton<br>
                      Anna Nolin<br>
                      Evaluator: Sue Sunbury<br>
                      <br>
                      <h4>WorldWide Telescope Team:</h4>
                      Peter Williams<br>
                      A. David Weigel<br>
                      Jon Carifio<br>
                      </div>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <funding-acknowledgement/>
                    </v-col>
                  </v-row>
                  <v-spacer class="end-spacer"></v-spacer>
                </v-container>              
              </v-card-text>
            </v-card>
          </v-window-item>
        </v-window>
      </v-card>
    </v-dialog>

    <v-container>
      <v-expand-transition>
        <user-experience
          v-if="showRating"
          :question="question"
          icon-size="3x"
          @dismiss="(_rating: UserExperienceRating | null, _comments: string | null) => {
            showRating = false;
          }"
          @rating="(rating: UserExperienceRating | null) => {
            currentRating = rating;
            updateUserExperienceInfo(currentRating, currentComments);
          }"
          @finish="(rating: UserExperienceRating | null, comments: string | null) => {
            currentRating = rating;
            currentComments = comments;
            updateUserExperienceInfo(currentRating, currentComments);
            showRating = false;
          }"
        >
          <template #footer>
            <div id="user-experience-footer" class="mt-4">
              <v-btn
                  class="rating-opt-put"
                  color="#BDBDBD"
                  size="small"
                  variant="text"
                  @click="onOptOutClicked"
                >
                Don't show again
              </v-btn>
              <v-btn
                class="privacy-button"
                color="#BDBDBD"
                @click="showPrivacyPolicy = true"
                @keyup.enter="showPrivacyPolicy = true"
                size="small"
                target="_blank"
                rel="noopener noreferrer"
              >
              What is this?
              </v-btn>
            </div>
          </template>
        </user-experience>
      </v-expand-transition>
      <cds-privacy-policy v-model="showPrivacyPolicy" />
    </v-container>

  </div>
</v-app>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { API_BASE_URL, MiniDSBase, BackgroundImageset, skyBackgroundImagesets, D2R, type UserExperienceRating } from "@cosmicds/vue-toolkit";
import { Annotation, Color, PolyLine, SpaceTimeController, SpreadSheetLayer, WWTControl } from "@wwtelescope/engine";
import { v4 } from "uuid";

// Coordinates isn't exposed to TypeScript, but it IS exported at the JS module level,
// so it's enough to do this. We just won't get any LSP help from an editor.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { Coordinates } from "@wwtelescope/engine";
import { GotoRADecZoomParams } from "@wwtelescope/engine-pinia";
import { AltTypes, AltUnits, MarkerScales, RAUnits } from "@wwtelescope/engine-types";

import { zoom } from "./wwt-hacks";

import sunCsv from "./assets/Sun_radec.csv";
import bestFitCsv from "./assets/radwave/RW_best_fit_oscillation_phase_radec_downsampled.csv";

function asyncSetTimeout<R>(func: () => R , ms: number): Promise<R> {
  return new Promise((resolve) => {
    setTimeout(() => resolve(func()), ms);
  });
}

function asyncWaitForCondition(func: () => boolean, ms: number): Promise<void> {
  return new Promise((resolve) => {
    const interval = setInterval(() => {
      if (func()) {
        clearInterval(interval);
        resolve();
      }
    }, ms);
  });
}

type SheetType = "text" | "video" | null;

// A hack, but we don't need anything more than this
type Row = Record<string, number>;

const SECONDS_PER_DAY = 86400;

let bestFitAnnotations: PolyLine[] = [];
const bestFitOffsets3D = [-2, -1, 0, 1, 2];
const bestFitOffsets2D = [0];
let bestFitOffsets = bestFitOffsets3D ;
const bestFitLayer = new SpreadSheetLayer();
const startDate = new Date("2023-10-18 11:55:55Z");
const endDate = new Date("2025-10-06 11:55:55Z");
const startTime = startDate.getTime();
const endTime = endDate.getTime();

let phase = 0;
let altFactor = 1;
let mode = "3D" as "2D" | "3D" | "full" | null;

const phaseRowCount = 300;

function getCurrentPhaseInfo(): [number, number] {
  const now = SpaceTimeController.get_now().getTime();
  const interval = 8.64e7;  // 1 day in ms
  const intervals = Math.floor((now - startTime) / interval);
  return [Math.floor(intervals / 360), intervals % 360];
}

function updateBestFitAnnotations(phase: number): void {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  bestFitAnnotations.forEach(ann => WWTControl.scriptInterface.removeAnnotation(ann));
  bestFitAnnotations = [];
  bestFitOffsets.forEach(offset => {
    const offsetPhase = (phase + offset) % 360;
    const ann = new PolyLine();
    ann.set_lineColor("#83befb");
    addPhasePointsToAnnotation(bestFitLayer, ann, offsetPhase);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    WWTControl.scriptInterface.addAnnotation(ann);
    bestFitAnnotations.push(ann);
  });
}


function addPhasePointsToAnnotation(layer: SpreadSheetLayer, annotation: Annotation, phase: number) {
  const lngCol = layer.get_lngColumn();
  const latCol = layer.get_latColumn();
  const dCol = layer.get_altColumn();
  const ecliptic = Coordinates.meanObliquityOfEcliptic(SpaceTimeController.get_jNow()) / 180 * Math.PI;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const rows: Row[] = layer.get__table().rows.slice(phase * phaseRowCount, (phase + 1) * phaseRowCount);
  for (const row of rows) {
    // The API for annotations seem to assume that we're in 2D sky mode - there's no option for distance
    // so we have to calculate our positions in 3D and just directly insert them into the array of points.
    // This of course necessitates some TypeScript hackery.
    // These calculations are stolen from around here: https://github.com/Carifio24/wwt-webgl-engine/blob/master/engine/esm/layers/spreadsheet_layer.js#L706
    let alt = row[dCol];
    alt = (altFactor * alt);
    const pos = Coordinates.geoTo3dRad(row[latCol], row[lngCol], alt);
    if (mode == "3D") {
      pos.rotateX(ecliptic);
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    annotation._points$1.push(pos);
  }
}



function updateSlider(value: number) {
  const input = document.querySelector("#time-slider");
  if (input) {
    const slider = input as HTMLInputElement;
    slider.value = String(value);
  }
}

export default defineComponent({
  extends: MiniDSBase,
  
  props: {
    wwtNamespace: {
      type: String,
      required: true
    },
    initialCameraParams: {
      type: Object as PropType<Omit<GotoRADecZoomParams, 'instant'>>,
      default() {
        return {
          raRad: 271.87846654 * D2R,
          decRad: -48.42 * D2R,
          zoomDeg: 289555092.0 * 6
        };
      }
    }
  },
  data() {
    const search = new URLSearchParams(window.location.search);
    const showSplashScreen = search.get("splash")?.toLowerCase() !== "false";
    const introQuery = search.get("intro")?.toLowerCase() !== "false";
    const userNotReady = introQuery;
    const initialOpacity = 0.2;
    const fadeStartPhase = 100;
    const fadeEndPhase = 270;
    const phaseOpacitySlope = -initialOpacity / (fadeEndPhase - fadeStartPhase);
    const phaseOpacityIntercept = initialOpacity * fadeEndPhase / (fadeEndPhase - fadeStartPhase);
    const initial2DPosition = {
      raRad: 6,
      decRad: 1,
      zoomDeg: 360
    } as Omit<GotoRADecZoomParams,'instant'>;
    
    const mode = "3D";

    const fullwavePosition = {
      raRad: 0.6984155220905679, 
      decRad: 0.7132099678793872, 
      rollRad: 0.183,
      zoomDeg: 360,
      instant: true
    }  as GotoRADecZoomParams;

    const maybeUUID = window.localStorage.getItem("cds-radwave-uuid");
    const uuid = maybeUUID ?? v4();
    const ratingOptedOut = window.localStorage.getItem("cds-radwave-rating-optout")?.toLowerCase() === "true";

    if (!showSplashScreen) {
      this.closeSplashScreen();
    }
    
    return {
      showSplashScreen,
      backgroundImagesets: [] as BackgroundImageset[],
      sheet: null as SheetType,
      layersLoaded: false,
      positionSet: false,
      
      userNotReady,
      
      accentColor: "#427cff",
      accentColor2: "#FF0000",
      buttonColor: "#ffffff",

      tab: 0,
      playing: false,
      timeRate: 120 * SECONDS_PER_DAY,
      playCount: 0,

      phaseCol: 3,
      clusterLayers: [] as SpreadSheetLayer[],

      initialOpacity,
      fadeStartPhase,
      fadeEndPhase,
      phaseOpacitySlope,
      phaseOpacityIntercept,
      clusterColor: "#1f3cf1",
      defaultClusterDecay: 15,
      opacity2Dto3DFactor: 2.5,

      sunColor: "#ffff0a",
      sunLayer: null as SpreadSheetLayer | null,
      
      modeReactive: mode as "2D" | "3D" | "full" | null,
      resizeObserver: null as ResizeObserver | null,
      background2DImageset: "Deep Star Maps 2020",
      position3D: this.initialCameraParams as Omit<GotoRADecZoomParams,'instant'>,
      position2D: initial2DPosition as Omit<GotoRADecZoomParams,'instant'>,
      initial2DPosition,
      allSkyImagesets: [
        'Deep Star Maps 2020', 
        'Mellinger color optical survey',  // HIPS
        "Gaia DR2",
        'Digitized Sky Survey (Color)',
        'unWISE color, from W2 and W1 bands', // HIPS
        'WISE All Sky (Infrared)',
        'Planck Thermal Dust',
        'Hydrogen Alpha Full Sky Map',
        "PLANCK R2 HFI color composition 353-545-857 GHz", // HIPS
      ],
      previousMode: mode as "2D" | "3D" | "full" | null,
      fullwavePosition: fullwavePosition,

      minZoom: 160763995.5927744,
      maxZoom: 22328103718.39476,

      ratingOptedOut,
      locationErrorMessage: "",
      showRating: false,
      showPrivacyPolicy: false, 
      storyRatingUrl: `${API_BASE_URL}/radwave-in-motion/user-experience`,
      uuid,
      currentRating: null as UserExperienceRating | null,
      currentComments: null as string | null,
      question: Math.random() > 0.5 ?
        "Does this spark your curiosity?" :
        "Are you learning something new?",
    };
  },

  mounted() {
    this.waitForReady().then(async () => {
      
      this.backgroundImagesets = [...skyBackgroundImagesets];
      
      // initialize the view to black so that we don't flicker DSS
      this.applySetting(["galacticMode", true]);
      this.loadHipsWTML().then(() => {
        console.log('init set3DMode');
        return this.set3DMode();
      }).then(() => this.positionSet = true);

      this.applySetting(["showConstellationBoundries", false]);  // Note that the typo here is intentional
      this.applySetting(["solarSystemStars", false]);
      this.applySetting(["actualPlanetScale", true]);
      this.applySetting(["showConstellationFigures", false]);
      this.applySetting(["showCrosshairs", false]);
      this.applySetting(["solarSystemCosmos", false]);
      // this.applySetting(["solarSystemPlanets", false]);
      this.setClockSync(false);

      this.setupBestFitLayer();
      const sunPromise = this.setupSunLayer();
      const clusterPromise = this.setupClusterLayers();

      Promise.all([sunPromise, clusterPromise]).then(([sunLayer, clusterLayers]) => {
        this.sunLayer = sunLayer;
        this.clusterLayers = clusterLayers;
        this.setTime(startDate);
        updateSlider(phase);
        window.requestAnimationFrame(this.onAnimationFrame);
        this.layersLoaded = true;
      });

      // if (!this.userNotReady) {
      //   this.ratingDisplaySetup();
      // }
      
    });
    this.resizeObserver = new ResizeObserver((_entries) => {
      this.shrinkWWT();
    });

    // Pin the min and max zoom in 3D mode
    WWTControl.singleton.setSolarSystemMinZoom(this.minZoom);
    WWTControl.singleton.setSolarSystemMaxZoom(this.maxZoom);

    // Patch the zoom function to account for min zoom as well
    // See upstream fix at https://github.com/WorldWideTelescope/wwt-webgl-engine/pull/292
    WWTControl.singleton.zoom = zoom.bind(WWTControl.singleton);
    
  },

  computed: {

    /**
    These properties relate to the state of the data story.
    `isLoading` is a bit redundant here, but it could potentially have
    independent logic.
    */
    ready(): boolean {
      return this.layersLoaded && this.positionSet;
    },
    isLoading(): boolean {
      return !this.ready;
    },

    /**
    Properties related to device/screen characteristics
    */
    smallSize(): boolean {
      return this.$vuetify.display.smAndDown;
    },

    xSmallSize(): boolean {
      return this.$vuetify.display.xs;
    },

    mobile(): boolean {
      return this.smallSize && this.touchscreen;
    },

    /**
    This lets us inject component data into element CSS
    */
    cssVars() {
      return {
        '--accent-color': this.accentColor,
        '--accent-color2': this.accentColor2,
        '--app-content-height': this.showTextSheet ? '66%' : '100%',
      };
    },

    /**
    Computed flags that control whether the relevant dialogs display.
    The `sheet` data member stores which sheet is open, so these are just
    computed wrappers around modifying/querying that which can be used as
    dialog v-model values
    */
    showTextSheet: {
      get(): boolean {
        return this.sheet === 'text';
      },
      set(_value: boolean) {
        this.selectSheet('text');
      }
    },
    showVideoSheet: {
      get(): boolean {
        return this.sheet === "video";
      },
      set(value: boolean) {
        this.selectSheet('video');
        if (!value) {
          const video = document.querySelector("#info-video") as HTMLVideoElement;
          video.pause();
        }
      }
    },
    
    wwtPosition(): Omit<GotoRADecZoomParams,'instant'> {
      return {
        raRad: this.wwtRARad,
        decRad: this.wwtDecRad,
        rollRad: this.wwtRollRad,
        zoomDeg: this.wwtZoomDeg,
      };
    }
  },

  methods: {
    closeSplashScreen() {
      this.showSplashScreen = false; 
      // Promise based wait for isLoading to be false
      asyncWaitForCondition(() => (!this.isLoading && !this.userNotReady), 100).then(() => {
        setTimeout(() => {
          this.playing = true;
        }, 500);
      });
      
    },
    
    async loadHipsWTML () {
      return this.loadImageCollection({
        url: "https://raw.githubusercontent.com/cosmicds/cds-website/main/public/wwt-content/radwave-wtml/hips.wtml",
        loadChildFolders: true,
      });
    },

    set2DMode() {
      
      bestFitOffsets = bestFitOffsets2D;
      this.clusterLayers.forEach(layer => {
        layer.set_decay(1);
        layer.set_opacity(this.opacity2Dto3DFactor * layer.get_opacity());
      });
      
      this.setBackgroundImageByName(this.background2DImageset);
      this.applySetting(["showSolarSystem", false]);
      this.sunLayer?.set_opacity(0);
      this.playing = false;
      phase = 0;
      updateSlider(phase);
      updateBestFitAnnotations(phase);    
      this.setTime(startDate);

      return asyncSetTimeout(() => {
        
        this.gotoRADecZoom({
          ...this.position2D,
          instant: true
        }).catch((err) => {
          console.log(err);
        
        }); 
      }, 100);

    },
    
    set3DMode() {
      
      bestFitOffsets = bestFitOffsets3D;
      this.clusterLayers.forEach(layer => {
        layer.set_decay(this.defaultClusterDecay);
        layer.set_opacity(layer.get_opacity() / this.opacity2Dto3DFactor);
      });

      this.setBackgroundImageByName("Solar System");
      this.setForegroundImageByName("Solar System");
      this.sunLayer?.set_opacity(1);
      updateBestFitAnnotations(phase);

      return this.gotoRADecZoom({
        ...this.position3D,
        instant: true,
      }).catch((err) => {
        console.log(err);
      });
      
    },
    
    async toggleFullwaveMode() {
      // to view in the full wave you need to adjust the height
      // of the window/canvas to have an W:H ration of 5.7
      let old3D = null as unknown as Omit<GotoRADecZoomParams,'instant'>;
      if (this.modeReactive == 'full') {
        this.modeReactive = this.previousMode;
        this.positionReset();
        return;
      } else if (this.modeReactive == '3D') {
        old3D = this.wwtPosition;
      }
      
      return this.set2DMode().then(() => {
        this.previousMode = this.modeReactive;
        this.modeReactive = "full";
        phase=0;
        
        this.shrinkWWT();
        this.resizeObserver?.observe(document.body);
        
        return this.gotoRADecZoom({
          ...this.fullwavePosition, 
          instant: false}).catch((err) => {
          console.log(err);
        }).then(() => {
          if (old3D) {this.position3D = old3D;}
        });
      });
      
    }, 
    
    toggleUI() {
      // toggle visibility of class .bottom-content using opacity
      const bottomContent = document.querySelector(".bottom-content") as HTMLElement;
      const op = bottomContent.style.opacity;
      if (op == "0") {
        bottomContent.style.opacity = "1";
      } else {
        bottomContent.style.opacity = "0";
      }
      
      // now for top-content
      const topContent = document.querySelector(".top-content") as HTMLElement;
      const op2 = topContent.style.opacity;
      if (op2 == "0") {
        topContent.style.opacity = "1";
      } else {
        topContent.style.opacity = "0";
      }
    },
    
    positionReset() {
      // since we aren't changing modes, 
      // we don't need to use set2DMode or set3DMode
      // also those are locked to only work if the mode is chaning
      // so we let's do this manually. 
      
      // only reset the current mode
      let pos = null as unknown as Omit<GotoRADecZoomParams, "instant">;
      if (this.modeReactive == "2D") {
        this.position2D = this.initial2DPosition;
        pos = this.position2D;
        
      } else if (this.modeReactive == "3D") {
        this.position3D = this.initialCameraParams;
        pos = this.position3D; 
      } else if (this.modeReactive == 'full') {
        pos = this.fullwavePosition;
      }
      
      // we will move nicely. 
      this.gotoRADecZoom({
        ...pos,
        instant: false
      }).catch((err) => {
        console.log(err);
      });
      
      
    },

    timeReset() {
      this.playing = false;
      phase = 0;
      const time = startTime;
      updateSlider(phase);
      updateBestFitAnnotations(phase);
      this.setTime(new Date(time));
    },
    
    shrinkWWT(aspect: number = null as unknown as number) {
      // default aspect = 5.7
      if (aspect == null) {
        aspect = 5.7;
      }
      console.log('shrinkWWT');
      
      const mainContent = document.querySelector(".wwtelescope-component") as HTMLElement;
      const width = mainContent.clientWidth;
      const height = width / aspect;
      mainContent.style.height = `${height}px`;
    },
    
    growWWT() {
      const mainContent = document.querySelector(".wwtelescope-component") as HTMLElement;
      mainContent.style.height = `100%`;
      this.resizeObserver?.unobserve(document.body as HTMLElement);
    },

    
    selectSheet(name: SheetType) {
      if (this.sheet === name) {
        this.sheet = null;
        this.$nextTick(() => {
          this.blurActiveElement();
        });
      } else {
        this.sheet = name;
      }
    },

    basicLayerSetup(layer: SpreadSheetLayer, timeSeries=false) {
      layer.set_lngColumn(0);
      layer.set_latColumn(1);
      layer.set_altColumn(2);
      layer.set_raUnits(RAUnits.degrees);
      layer.set_altUnit(AltUnits.parsecs);
      layer.set_altType(AltTypes.distance);
      layer.set_showFarSide(true);
      layer.set_markerScale(MarkerScales.screen);
    
      if (timeSeries) {
        layer.set_startDateColumn(4);
        layer.set_endDateColumn(5);
        layer.set_timeSeries(true);
        layer.set_decay(this.defaultClusterDecay);
      }
    },

    async setupSunLayer(): Promise<SpreadSheetLayer> {
      const text = sunCsv.replace(/\n/g, "\r\n");
      return this.createTableLayer({
        referenceFrame: "Sky",
        name: "Radcliffe Wave Sun",
        dataCsv: text
      }).then(layer => {
        this.basicLayerSetup(layer, true);
        layer.set_decay(1);
        this.applyTableLayerSettings({
          id: layer.id.toString(),
          settings: [
            ["color", Color.load(this.sunColor)],
            ["scaleFactor", 100]
          ]
        });
        return layer;
      });
    },

    // Note that we are NOT going to show this layer
    // (we aren't even going to put it in the layer manager)
    // But by doing this we can both hijack WWT's CSV-parsing functionality
    // as well as get the scale factor that we need for scaling the annotation points
    setupBestFitLayer() {
      const text = bestFitCsv.replace(/\n/g, "\r\n");
      // This isn't exposed to TS
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      bestFitLayer.loadFromString(text, false, false, false, true);
      this.basicLayerSetup(bestFitLayer, true);

      // Another method that's not exposed to TS
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore

      altFactor = bestFitLayer.getScaleFactor(bestFitLayer.get_altUnit(), 1);
      altFactor = altFactor / (1000 * 149598000);
    },

    opacityForPhase(phase: number): number {
      return Math.min(Math.max(this.phaseOpacitySlope * phase + this.phaseOpacityIntercept, 0), this.initialOpacity);
    },

    setupClusterLayers(): Promise<SpreadSheetLayer[]> {
      const color = Color.load(this.clusterColor);
      const promises: Promise<SpreadSheetLayer>[] = [];
      for (let phase = -15; phase <= this.fadeEndPhase; phase++) {
        const prom = import(`./assets/radwave/RW_cluster_oscillation_${phase}_updated_radec.csv`).then(res => {
          let text = res.default;
          text = text.replace(/\n/g, "\r\n");
          return this.createTableLayer({
            referenceFrame: "Sky",
            name: `Radcliffe Wave Clusters Phase ${phase}`,
            dataCsv: text
          }).then(layer => {
            this.basicLayerSetup(layer, true);
            layer.set_opacity(this.opacityForPhase(phase));
            layer.set_color(color);
            layer.set_scaleFactor(70);
            //console.log(layer);
            return layer;
          });
        });
        promises.push(prom);
      }
      return Promise.all(promises);
    },

    onInputChange(event: InputEvent) {
      this.playing = false;
      const target = event.target as HTMLInputElement;
      const value = Number(target.value);
      if (!isNaN(value)) {
        phase = Math.max(0, Math.min(value, 720)); 
        const time = startTime + (value / 720) * (endTime - startTime);
        updateBestFitAnnotations(phase);
        this.setTime(new Date(time)); 
      }
    },
    
    onAnimationFrame(_timestamp: DOMHighResTimeStamp) {
      let newPhase = phase;
      if (SpaceTimeController.get_syncToClock()) {
        if (SpaceTimeController.get_now() >= endDate) {
          SpaceTimeController.set_now(startDate);
          this.playing = false;
          this.playCount += 1;
        } 
        const [currPeriod, currPhase] = getCurrentPhaseInfo();
        newPhase = currPeriod * 360 + currPhase;
      }
      if (newPhase !== phase) {
        phase = newPhase;
        updateBestFitAnnotations(phase);
        updateSlider(phase);
      }
      window.requestAnimationFrame(this.onAnimationFrame);
    },

    async ratingDisplaySetup() {
      if (this.ratingOptedOut) {
        return;
      }
      const existsResponse = await fetch(`${this.storyRatingUrl}/${this.uuid}`, {
        method: "GET",
        // eslint-disable-next-line @typescript-eslint/naming-convention
        headers: { "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "" }
      });
      // NB: If we want to ask multiple questions, this logic can be adjusted
      const existsContent = await existsResponse.json();
      const exists = existsResponse.status === 200 && existsContent.ratings?.length > 0;
      if (exists) {
        return;
      }
      setTimeout(() => {
        this.showRating = true;
      }, 30_000);
    },

    updateUserExperienceInfo(rating: UserExperienceRating | null, comments: string | null) {
      const body: Record<string, unknown> = {
        uuid: this.uuid,
        question: this.question,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        story_name: "radwave-in-motion",
      };
      if (rating) {
        body.rating = rating;
      }
      if (comments) {
        body.comments = comments;
      }
      fetch(this.storyRatingUrl, {
        method: "PUT",
        headers: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          "Authorization": process.env.VUE_APP_CDS_API_KEY ?? "",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
    },
    onOptOutClicked() {
      this.showRating = false;
      this.ratingOptedOut = true;
      window.localStorage.setItem("cds-radwave-rating-optout", "true");
    },
  },

  watch: {
    playing(play: boolean) {
      if (!play) {
        this.playCount += 1;
      }
      this.setClockSync(play);
      this.setClockRate(play ? this.timeRate : 0);
    },
    
    background2DImageset(name: string) {
      
      if (this.modeReactive == "2D") {
        this.setBackgroundImageByName(name);
        return;
      }
      
    },
    
    // // positin3d deep
    // position3D: {
    //   handler(newVal) {        
    //     if (this.mode == "3D") {
    //       console.log('moving');
    //       this.gotoRADecZoom({
    //         ...newVal,
    //         instant: true
    //       }).catch((err) => {
    //         console.log(err);
    //       });
    //     }
    //   },
    //   deep: true
    // },
    
    // // positin2d deep
    // position2D: {
    //   handler(newVal) {
    //     if (this.mode == "2D") {
    //       this.gotoRADecZoom({
    //         ...newVal,
    //         instant: true
    //       }).catch((err) => {
    //         console.log(err);
    //       });
    //     }
    //   },
    //   deep: true
    // },
    
    modeReactive(newVal, oldVal) {
      mode = newVal;
      if (oldVal == newVal) {
        if (newVal == "2D") {
          this.set2DMode();
        }
        if (newVal == "3D") {
          this.set3DMode();
        }
        if (newVal == "full") {
          this.toggleFullwaveMode();
        }
      } else {
      
        if (oldVal == "2D") {
          this.position2D = this.wwtPosition;
        } else if (oldVal == "3D") {
          this.position3D = this.wwtPosition;
        } else if (oldVal == "full") {
          this.resizeObserver?.disconnect();
          this.growWWT();
          this.toggleUI();
        }
      }
      
      
      
      if (newVal == "2D") {
        this.set2DMode();
      } else if (newVal == "3D") {
        this.set3DMode();
      } else {
        // don't do anything if mode is null
        return;
      }
      
    },

    // userNotReady(value: boolean) {
    //   if (!value) {
    //     this.ratingDisplaySetup();
    //   }
    // }
    
  }
});
</script>

<style lang="less">



//  =====================================

#splash-screen #close-splash-button {
  position: absolute;
  top: 20px;
  right: 20px;
  text-align: end;
  color: var(--accent-color-2);
  font-size: min(5vw, 4vh);

  &:hover {
    cursor: pointer;
  }
}

// =====================================

#modal-loading {

  background-color: #000;

  @media (max-width: 699px) {
    background-image: url("./assets/radwave_landing_mobile.png");
  }

  @media (min-width: 700px) {
    background-image: url("./assets/radwave_landing_desktop.png");
  }

  background-position: center;
  background-size: 100%;
  background-repeat: no-repeat;
  
  
  > div {
    position: absolute;
    right: 1rem;
    bottom: 1rem;
  }
  
  .container {
    background-color: rgba(0, 0, 0, 0.5);
    padding-inline: 2rem;
    padding-block: 1rem;
    border-radius: 1rem;
    .spinner {
      background-image: none !important;
      display: none;
    }
  }
}

/* Top and bottom content */
.top-content {
  position: absolute;
  top: 1rem;
  left: 1rem;
  width: calc(100% - 2rem);
  pointer-events: none;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.bottom-content {
  display: flex;
  flex-direction: column;
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  width: calc(100% - 2rem);
  pointer-events: none;
  align-items: center;
  gap: 5px;
}

#left-buttons, #right-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

#center-buttons {
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
}

#splash-screen {
  color: #E0E0E0;

  @media (max-width: 699px) {
    max-height: 80vh;
    max-width: 90vw;
  }

  @media (min-width: 700px) {
    max-height: 85vh;
    max-width: min(70vw, 800px);
  }
  
  background-color: black;
  backdrop-filter: blur(5px);
  justify-content: space-around;
  align-content: center;
  padding-top: 4rem;
  padding-bottom: 1rem;

  border-radius: 30px;
  border: min(1.2vw, 0.9vh) solid #6facf1;
  overflow: auto;
  font-family: 'Highway Gothic Narrow', 'Roboto', sans-serif;
}

.splash-get-started {
  border: 2px solid white;
  font-size: calc(1.8 * var(--default-font-size)) !important;
  margin-top: 5%;
  margin-bottom: 2%;
  font-weight: bold !important;
}

#splash-screen-acknowledgements {
  margin-top: 2rem;
  font-size: .5em;
  width: 70%; 
}

#loading-button {
  background-color: black;
}

.color-span {
  color:var(--accent-color);
}

#time-controls {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  .icon-wrapper {
    white-space: nowrap;
  }
  
  #play-pause-icon-button {
    padding-inline: 1.5rem;
    padding-block: 0.75rem;
  }
  
  // style input slider
  #time-slider[type=range] {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    margin: 0;
    padding: 0;
    height: 1.25rem;
    border-radius: .5rem;
    background: rgba(255, 255, 255, 50%);
    pointer-events: auto;
  }
  
  
  
  .thumb-style {
    -webkit-appearance: none;
    appearance: none;
    width: 1.25rem;
    height: 1.25rem;
    border-radius: 50%;
    background: #fff;
    cursor: pointer;
    pointer-events: auto;
  }
  
  // LESS allows mixins https://lesscss.org/features/#mixins-feature
  // this will put the content of .thumb-style into the following selectors
  #time-slider[type=range]::-webkit-slider-thumb {
    .thumb-style();
  }
  #time-slider[type=range]::-moz-range-thumb {
    .thumb-style();
  }
  
  
}

#time-slider {
  width: 100%;
  pointer-events: auto;
}

.disabled {
  pointer-events: none;
}

.v-leave-active {
  transition: opacity 1s ease;
}

.v-leave-to {
  opacity: 0;
}


video, #info-video {
  margin: auto;
  max-width: 100%;
  object-fit: contain;

}

.rating-root {
  position: absolute !important;
  right: 5px;
  bottom: 0;
  padding: 5px;
  width: fit-content !important;
  // left: 50%;
  // transform: translateX(-50%);
  gap: 0 !important;
  border: solid 1px #EFEFEF !important;
  border-radius: 10px !important;
  background-color: #222222 !important;
  opacity: 0.95 !important;
  z-index: 20000;
  .rating-title {
    color: #EFEFEF;
    font-size: var(--default-font-size);
  }
  .rating-icon-row {
    
    padding: 0px;
    .svg-inline--fa {
      height: 30px;
    }
  }
  .comments-box {
    width: 100%;
    margin-top: 20px;
  }
  .v-card-text {
    padding-bottom: 0;
  }
  .v-card-actions {
    padding: 0;
  }
   #user-experience-footer {
    display: flex;
    flex-direction: row;
    gap: 5px;
  }
  .close-button {
    position: absolute !important;
    color: white !important;
  }
  .v-field--variant-filled .v-field__outline:before, .v-field--variant-underlined .v-field__outline:before, 
  .v-field--variant-filled .v-field__outline:after, .v-field--variant-underlined .v-field__outline:after {
    border-style: none !important;
  }
}
</style>
